import axios from '../helpers/axios'
import guestApi from '../helpers/guestApi'
import { inferMediaMimeType } from './mediaDocument'

const encode = (value) => encodeURIComponent(String(value || '').trim())
const CACHE_MAX_ITEMS = 48
const CACHE_MAX_BYTES = 96 * 1024 * 1024

const attachmentCache = new Map()
let attachmentCacheBytes = 0

const getAuthScope = (guestToken = '') => {
    const guest = String(guestToken || '').trim()
    if (guest) return `guest:${guest}`

    if (typeof window === 'undefined') return 'auth:server'

    return `auth:${String(window.localStorage.getItem('token') || '')}`
}

const getCacheKey = (messageId, guestToken = '') => (
    `${getAuthScope(guestToken)}:${String(messageId || '')}`
)

const touchCacheEntry = (key, entry) => {
    attachmentCache.delete(key)
    attachmentCache.set(key, entry)
}

const pruneCache = () => {
    while (attachmentCache.size > CACHE_MAX_ITEMS || attachmentCacheBytes > CACHE_MAX_BYTES) {
        const first = attachmentCache.entries().next().value
        if (!first) break

        const [key, entry] = first
        attachmentCache.delete(key)
        attachmentCacheBytes = Math.max(0, attachmentCacheBytes - Number(entry?.size || 0))
    }
}

const setCachedAsset = (key, asset) => {
    if (!asset?.blob) return asset

    const previous = attachmentCache.get(key)
    if (previous) {
        attachmentCacheBytes = Math.max(0, attachmentCacheBytes - Number(previous.size || 0))
    }

    const entry = {
        asset,
        size: Number(asset.blob.size || 0),
        createdAt: Date.now(),
    }

    attachmentCache.set(key, entry)
    attachmentCacheBytes += entry.size
    pruneCache()
    return asset
}

export const clearChatAttachmentCache = () => {
    attachmentCache.clear()
    attachmentCacheBytes = 0
}

export const hasSecureChatAttachment = (message = {}) => !!(
    message?.hasAttachment ||
    message?.fileName ||
    message?.fileType ||
    message?.fileMime ||
    message?.fileUrl ||
    message?.secureAttachment?.kind
)

export const getChatAttachmentName = (message = {}, fallback = 'Вложение') => (
    String(
        message?.fileName ||
        message?.secureAttachment?.originalName ||
        ''
    ).trim() || fallback
)

export const getChatAttachmentMimeType = (message = {}) => (
    inferMediaMimeType({
        mimeType: String(
            message?.fileMime ||
            message?.secureAttachment?.mimeType ||
            ''
        ).trim().toLowerCase(),
        name: getChatAttachmentName(message, ''),
    })
)

export const getChatAttachmentType = (message = {}) => {
    const explicit = String(message?.fileType || '').trim().toLowerCase()
    if (['image', 'video', 'voice', 'doc'].includes(explicit)) return explicit

    const mime = getChatAttachmentMimeType(message)
    const name = getChatAttachmentName(message, '').toLowerCase()

    if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(name)) return 'image'
    if (mime.startsWith('video/') || /\.(mp4|mov|webm|mkv|avi)$/i.test(name)) return 'video'
    if (mime.startsWith('audio/') || /\.(webm|ogg|oga|mp3|m4a|aac|wav)$/i.test(name)) return 'voice'

    return 'doc'
}

const getFilenameFromDisposition = (headerValue, fallback) => {
    const value = String(headerValue || '')
    const utfMatch = value.match(/filename\*=UTF-8''([^;]+)/i)

    if (utfMatch?.[1]) {
        try {
            return decodeURIComponent(utfMatch[1])
        } catch (_error) { }
    }

    const plainMatch = value.match(/filename="?([^";]+)"?/i)
    return plainMatch?.[1] || fallback
}

export const fetchChatAttachment = async ({
    message,
    guestToken = '',
    download = false,
    useCache = true,
} = {}) => {
    const messageId = String(message?._id || message?.id || '').trim()

    if (!messageId) throw new Error('Не найден идентификатор сообщения')

    const cacheKey = getCacheKey(messageId, guestToken)

    if (!download && useCache) {
        const cached = attachmentCache.get(cacheKey)
        if (cached?.asset?.blob) {
            touchCacheEntry(cacheKey, cached)
            return cached.asset
        }
    }

    const suffix = download ? '/download' : ''
    const fallbackName = getChatAttachmentName(message, `attachment-${messageId}`)
    const response = guestToken
        ? await guestApi.get(
            `/scene-guest-access/chat/messages/${encode(messageId)}/attachment${suffix}`,
            {
                sceneGuestToken: guestToken,
                responseType: 'blob',
                timeout: 60000,
            }
        )
        : await axios.get(
            `/messages/${encode(messageId)}/attachment${suffix}`,
            {
                responseType: 'blob',
                timeout: 60000,
            }
        )

    const asset = {
        blob: response.data,
        filename: getFilenameFromDisposition(
            response.headers?.['content-disposition'],
            fallbackName
        ),
        mimeType: inferMediaMimeType({
            mimeType: String(
                response.data?.type ||
                response.headers?.['content-type'] ||
                getChatAttachmentMimeType(message) ||
                'application/octet-stream'
            ).split(';')[0].trim().toLowerCase(),
            name: getFilenameFromDisposition(
                response.headers?.['content-disposition'],
                fallbackName
            ),
        }),
    }

    return !download && useCache
        ? setCachedAsset(cacheKey, asset)
        : asset
}

/**
 * Уменьшенная копия фото (small — до 400 px, medium — до 1280 px): в десятки
 * раз легче оригинала, поэтому сетка альбома появляется быстро. Сервер
 * старой версии копий не делает — тогда берём оригинал, как раньше.
 */
const previewCache = new Map()
let previewMisses = 0

export const fetchChatAttachmentPreview = async ({ message, size = 'small', guestToken = '' } = {}) => {
    const messageId = String(message?._id || message?.id || '').trim()
    if (!messageId) throw new Error('Не найден идентификатор сообщения')

    // Гостю копии не нужны: его чат открывает фото напрямую.
    if (guestToken || previewMisses >= 3) return fetchChatAttachment({ message, guestToken })

    const key = `${getAuthScope()}:${size}:${messageId}`
    const cached = previewCache.get(key)
    if (cached) return cached

    const job = axios.get(`/messages/${encode(messageId)}/attachment/preview`, {
        params: { size },
        responseType: 'blob',
        timeout: 60000,
    })
        .then((response) => {
            previewMisses = 0
            return {
                blob: response.data,
                filename: getChatAttachmentName(message, `photo-${messageId}.jpg`),
                mimeType: String(response.data?.type || 'image/jpeg'),
            }
        })
        .catch((error) => {
            previewCache.delete(key)
            const status = Number(error?.response?.status || 0)
            if (status === 404 || status === 501) previewMisses += 1
            if (status === 401 || status === 403) throw error
            return fetchChatAttachment({ message })
        })

    previewCache.set(key, job)
    // Копий держим немного: старые забываем.
    if (previewCache.size > 300) previewCache.delete(previewCache.keys().next().value)
    return job
}

export const downloadBlob = ({ blob, filename }) => {
    if (!blob || typeof window === 'undefined') return false

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = filename || 'attachment'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()

    window.setTimeout(() => URL.revokeObjectURL(url), 1500)
    return true
}

export const shareBlobFile = async ({ blob, filename, mimeType, title = '' } = {}) => {
    if (!blob || typeof navigator === 'undefined') return false

    const file = new File(
        [blob],
        filename || 'attachment',
        { type: mimeType || blob.type || 'application/octet-stream' }
    )

    if (typeof navigator.share === 'function') {
        const data = {
            title: title || filename || 'Вложение',
            files: [file],
        }

        if (typeof navigator.canShare !== 'function' || navigator.canShare(data)) {
            try {
                await navigator.share(data)
                return true
            } catch (error) {
                if (error?.name === 'AbortError') return false
                throw error
            }
        }
    }

    downloadBlob({ blob, filename })
    return false
}
